import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Card,
  TablePagination,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  CalendarToday as CalendarIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Close,
} from '@mui/icons-material';

// import { Close as CloseIcon, Upload } from '@mui/icons-material';

import apiService from '../services/api';
import dayjs from 'dayjs';
import FloorEditDialog from '../components/modals/EscCalenderEditDialog';
import {
  EscCalenderSingleValue,
  EventFilterParams,
  singleGuestValue,
  TableColumn,
} from '../types';
import { toast } from 'react-toastify';
import DataTable from '../components/dataTable';
import GuestEditDialog from '../components/modals/GuestEditDialog';
import { AvatarWrapper } from '../styles/globalStyle';

const Guest: React.FC = () => {
  const [guest, setGuest] = useState<singleGuestValue[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Filters
  const [filters, setFilters] = useState<EventFilterParams>({
    page: 1,
    limit: 25,
    search: '',
    isActive: undefined,
  });

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<singleGuestValue | null>(
    null
  );
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [guestToDelete, setGuestToDelete] = useState<singleGuestValue | null>(
    null
  );

  const fetchGuests = async () => {
    setLoading(true);
    try {
      const guestResponse = await apiService.getGuests(filters);
      setGuest(guestResponse.data || []);
      setTotal(guestResponse.total || 0);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch session');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGuests();
  }, [filters]);

  const handleEditEvent = (editGuestData: singleGuestValue) => {
    setEditingGuest(editGuestData);
    setEditDialogOpen(true);
  };

  const handleDeleteEvent = (event: singleGuestValue) => {
    setGuestToDelete(event);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!guestToDelete) return;

    try {
      const response = await apiService.deleteGuest(guestToDelete.id);
      toast.success('Guest deleted successfully');
      setDeleteDialogOpen(false);
      setGuestToDelete(null);
      fetchGuests();
    } catch (err: any) {
      const apiError =
        err?.response?.data?.message ||
        err?.message ||
        'Something went wrong. Please try again.';
      setError(apiError);
      toast.error(apiError);
    }
  };

  const handleSessionSaved = () => {
    fetchGuests();
    setEditDialogOpen(false);
    setEditingGuest(null);
  };

  const handleFilterChange = (field: keyof EventFilterParams, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
      page: 1,
    }));
    setPage(0);
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setFilters(prev => ({ ...prev, limit: newRowsPerPage, page: 1 }));
  };

  const columns: TableColumn<singleGuestValue>[] = [
    {
      label: 'Guest',
      keys: ['profiImgUrl', 'firstName', 'lastName'],
      renderGroup: values => {
        const [image, firstName, lastName] = values as [string, string, string];
        const fullName = `${firstName} ${lastName}`;

        const getInitials = (firstName: string, lastName: string) => {
          return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
        };

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AvatarWrapper>
              {image ? (
                <Avatar
                  className="avatar"
                  src={image}
                  alt={`${firstName} ${lastName}`}
                ></Avatar>
              ) : (
                <Avatar className="avatar" alt={`${firstName} ${lastName}`}>
                  {getInitials(firstName, lastName)}
                </Avatar>
              )}
            </AvatarWrapper>

            <Typography sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
              {fullName}
            </Typography>
          </Box>
        );
      },
    },

    {
      label: 'Email',
      key: 'email',
      renderGroup: values => {
        const [email] = values as [string];
        return (
          <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
            {email || '-'}
          </Typography>
        );
      },
    },

    {
      label: 'Phone',
      key: 'phone',
      renderGroup: values => {
        const [phone] = values as [string];
        return (
          <Typography sx={{ fontSize: '0.875rem', color: '#111827' }}>
            {phone || '-'}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Typography
          sx={{ fontSize: '1.875rem', fontWeight: 700, color: '#111827' }}
        >
          Guest Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          sx={{
            bgcolor: '#3b82f6',
            '&:hover': { bgcolor: '#2563eb' },
            textTransform: 'none',
            fontWeight: 500,
            px: 2,
            py: 1,
          }}
          onClick={() => setEditDialogOpen(true)}
        >
          Create Guest
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main Content Card */}
      <Card
        sx={{
          boxShadow:
            '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
          border: '1px solid #e5e7eb',
          borderRadius: 2,
        }}
      >
        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #e5e7eb' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flex: 1,
                flexWrap: 'wrap',
              }}
            >
              <TextField
                placeholder="Search Guests..."
                value={filters.search}
                onChange={e => handleFilterChange('search', e.target.value)}
                size="small"
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#6b7280', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
              />
              {/* <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={
                    filters.isActive === undefined
                      ? ''
                      : filters.isActive.toString()
                  }
                  onChange={e =>
                    handleFilterChange(
                      'isActive',
                      e.target.value === ''
                        ? undefined
                        : e.target.value === 'true'
                    )
                  }
                  label="Status"
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="true">Active</MenuItem>
                  <MenuItem value="false">Inactive</MenuItem>
                </Select>
              </FormControl> */}
            </Box>
          </Box>
        </Box>

        {/* Table */}
        <DataTable<singleGuestValue>
          columns={columns}
          data={guest}
          actions={row => [
            {
              icon: <EditIcon fontSize="small" />,
              onClick: () => handleEditEvent(row),
            },
            {
              icon: <DeleteIcon fontSize="small" />,
              onClick: () => handleDeleteEvent(row),
            },
          ]}
        />

        {/* Pagination */}
        {guest.length > 0 && (
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{
              borderTop: '1px solid #e5e7eb',
              '& .MuiTablePagination-toolbar': {
                px: 3,
              },
            }}
          />
        )}
      </Card>

      {/* Edit Dialog */}
      <GuestEditDialog
        open={editDialogOpen}
        guest={editingGuest}
        onClose={() => {
          setEditDialogOpen(false);
          setEditingGuest(null);
        }}
        onSave={handleSessionSaved}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              {' '}
              Confirm Delete{' '}
            </Typography>
          </Box>
          <IconButton
            onClick={() => setDeleteDialogOpen(false)}
            disabled={loading}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete Guest "
            {guestToDelete &&
              `${guestToDelete.firstName} ${guestToDelete.lastName}`}
            "? This action cannot be undone and will also remove all associated
            registrations and sessions.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(211, 47, 47, 0.08)',
              color: '#d32f2f',
              fontWeight: 500,
              '&:hover': {
                backgroundColor: 'rgba(211, 47, 47, 0.15)',
              },
            }}
          >
            Cancel
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Guest;
